"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.setupSGWTConnect = void 0;
var isScopeListed_1 = require("./isScopeListed");
var SGWTConnectConfiguration_1 = require("./SGWTConnectConfiguration");
var SGWTConnectCore_1 = require("./SGWTConnectCore");
var SGWTConnectErrorList_1 = require("./SGWTConnectErrorList");
var SGWTConnectHash_1 = require("./SGWTConnectHash");
var SGWTConnectNoop_1 = require("./SGWTConnectNoop");
var SGWTConnectOpenIDMetadata_1 = require("./SGWTConnectOpenIDMetadata");
var SGWTConnectRedirectUri_1 = require("./SGWTConnectRedirectUri");
var SGWTConnectServerResponse_1 = require("./SGWTConnectServerResponse");
var SGWTConnectStorage_1 = require("./SGWTConnectStorage");
function hashConfigForConnectStorageKey(config) {
    // The key storage - if not overriden by `storage_key` configuration - is generated with
    // the configuration given to setupSGWTConnect() function. To avoid any issue with newer
    // versions (the widget may use another version than the library embedded in the application)
    // we limit the properties considered to generate the hash.
    // In others terms, we do not include new configuration options (introduced after version 0.10.0)
    // for the hash generation.
    // Note: properties with `undefined` values will be removed in the `hash` function.
    var limitedConfig = {
        acr_values: config.acr_values,
        authorization_endpoint: config.authorization_endpoint,
        client_id: config.client_id,
        redirect_uri: config.redirect_uri,
        response_type: config.response_type,
        scope: config.scope,
        post_logout_redirect_uri: config.post_logout_redirect_uri,
    };
    return (0, SGWTConnectHash_1.hash)(limitedConfig);
}
/**
 * SGWTConnect instance factory.
 *
 * Determines the execution environment (top-level window or iframe),
 * instantiates the relevant SGWTConnect class with the given configuration,
 * and handles authorization redirects in the top-level window.
 */
function setupSGWTConnect(userConfig) {
    var defaultUserConfig = __assign(__assign({}, userConfig), { with_nonce: typeof userConfig.with_nonce === 'undefined' ? true : userConfig.with_nonce });
    var config = (0, SGWTConnectConfiguration_1.normalizeConfiguration)(defaultUserConfig, location);
    // Ensure that the stored token and other data have been obtained
    // using the current configuration options (note that we're not
    // using JSON.stringify here because it will generate different
    // results for identical configurations with different ordering
    // of keys in them):
    var storage = new SGWTConnectStorage_1.SGWTConnectStorage(userConfig.storage_key ? userConfig.storage_key : hashConfigForConnectStorageKey(config));
    // Check if we're currently on the redirect_uri page:
    if ((0, SGWTConnectRedirectUri_1.isOnRedirectUri)(config, location)) {
        // Check if we are currently in a silent renewal iframe:
        if (window.frameElement && window.frameElement.hasAttribute(SGWTConnectCore_1.SGWTConnectRenewalFrameAttribute)) {
            // We only message the top-level window if the iframe's URI
            // matches the redirect URI exactly (without the fragment):
            window.parent.postMessage({
                type: 'SGWTConnectFragment',
                fragment: location.hash,
            }, config.redirect_uri);
            // Because we are currently on the redirect_uri page, we must not
            // return the fully-functional API as this may result in undesired
            // behavior and infinite reload, but we still need to return a
            // compliant API to prevent JS runtime errors on the page, so we
            // return a dummy API where most methods are noop:
            return new SGWTConnectNoop_1.SGWTConnectNoop(config, storage);
        }
        // We are currently doing a hard redirect.
        // Extract the authorization server response from the current URL fragment:
        var authorization = (0, SGWTConnectServerResponse_1.normalizeAuthServerResponse)((0, SGWTConnectServerResponse_1.parseOAuth2Fragment)(window.location.hash));
        var stateInResponse = authorization.state;
        // Cross-Site Request Forgery (CSRF) protection:
        var stateInStore = storage.getItem("".concat(stateInResponse, ".STATE"));
        if (stateInResponse === stateInStore) {
            // Persist the authorization server response prior to emitting
            // events and calling the completion callback so that the token
            // is available to the completion event handlers (note that
            // the server response might be a failed one):
            storage.setItem('AUTH', authorization);
        }
        else if (stateInStore) {
            // The "state" parameter failed to match; it may be dangerous
            // to keep the received server response, so we replace it
            // with a custom "state_mismatch" error response:
            var authError = {
                error: SGWTConnectErrorList_1.ERR_STATE_MISMATCH,
            };
            storage.setItem('AUTH', authError);
        }
        // We are currently on the "redirect_uri", but the user most
        // probably initiated the authorization process from another
        // URI, so we need to redirect them back there (or to home
        // page if RETURN_URL has been cleared):
        var returnUrl = storage.getItem("".concat(stateInResponse, ".RETURN_URL")) || "".concat(window.location.protocol, "//").concat(window.location.host);
        // location.replace doesn't reload the page if only the fragment
        // has changed, so we need the following workaround to force
        // the page reload, but also to set the fragment of the returnUrl:
        if (returnUrl.indexOf('#') !== -1 && location.href.split('#')[0] === returnUrl.split('#')[0]) {
            window.location.hash = returnUrl.split('#')[1];
            window.location.reload();
        }
        else {
            window.location.replace(returnUrl);
        }
        // Because we are currently on the redirect_uri page, we must not
        // return the fully-functional API as this may result in undesired
        // behavior and infinite reload, but we still need to return a
        // compliant API to prevent JS runtime errors on the page, so we
        // return a dummy API where most methods are noop:
        return new SGWTConnectNoop_1.SGWTConnectNoop(config, storage);
    }
    // We are NOT on the redirect_uri page, do we have authorization?:
    var auth = storage.getItem('AUTH');
    // Clean up the storage of values needed during redirects:
    if (auth) {
        storage.removeItem("".concat(auth.state, ".STATE"));
        storage.removeItem("".concat(auth.state, ".RETURN_URL"));
    }
    // Clean up the storage of the expired tokens:
    storage.clearBy(function (value, key) {
        return key.indexOf('AUTH') !== -1 && value.expires_at < Date.now();
    });
    // At this point we know that this code is running in the top-level window,
    // (not in iframe) so we can instantiate a fully-functional API:
    var sgwtConnect = (0, isScopeListed_1.isScopeListed)('openid', config.scope)
        ? new SGWTConnectCore_1.SGWTConnectCore(config, storage, (0, SGWTConnectOpenIDMetadata_1.createOpenIDMetadataFetcher)(config.authorization_endpoint))
        : new SGWTConnectCore_1.SGWTConnectCore(config, storage);
    // Checking if the authorization has been explicitly granted:
    if (sgwtConnect.isAuthorized()) {
        // The user-friendly behavior is to have periodical renewal enabled by default:
        return sgwtConnect.enablePeriodicalRenewal();
    }
    // Checking if the authorization has been explicitly denied:
    if (auth && auth.error) {
        if (auth.markedForDeletion) {
            storage.removeItem('AUTH');
        }
        else {
            auth.markedForDeletion = true;
            storage.setItem('AUTH', auth);
        }
    }
    return sgwtConnect;
}
exports.setupSGWTConnect = setupSGWTConnect;
//# sourceMappingURL=setupSGWTConnect.js.map